import { Injectable } from '@angular/core';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { DailyLeadListResponse, DailyLeadShowResponse, DailyLeadRunsResponse, DailyLeadNotificationsResponse, GenerateLeadsResponse, IcpAdjustmentNotification } from '../pages/leads-legacy/lead.model';

@Injectable({
  providedIn: 'root'
})
export class LeadsLegacyService {

  private apiUrl = environment.api.url;
  private cancelRequests$ = new Subject<void>();

  private token: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  getLeads(params?: {
    filter?: { status?: string; icp_id?: number };
    sort?: string;
    per_page?: number;
    page?: number;
  }): Observable<DailyLeadListResponse> {
    return this.authService.token().pipe(
      takeUntil(this.cancelRequests$),
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          }),
          params: new HttpParams()
        };

        if (params?.filter?.status) {
          httpOptions.params = httpOptions.params.set('filter[status]', params.filter.status);
        }
        if (params?.filter?.icp_id) {
          httpOptions.params = httpOptions.params.set('filter[icp_id]', params.filter.icp_id.toString());
        }
        if (params?.sort) {
          httpOptions.params = httpOptions.params.set('sort', params.sort);
        }
        if (params?.per_page) {
          httpOptions.params = httpOptions.params.set('per_page', params.per_page.toString());
        }
        if (params?.page) {
          httpOptions.params = httpOptions.params.set('page', params.page.toString());
        }

        const url = `${this.apiUrl}/daily-leads`;

        return this.http.get<DailyLeadListResponse>(url, httpOptions).pipe(
          takeUntil(this.cancelRequests$),
          catchError(error => {
            console.error('Error fetching daily leads', error);
            return throwError(() => error);
          })
        );
      })
    );
  }

  getLeadDetails(id: string): Observable<DailyLeadShowResponse> {
    return this.authService.token().pipe(
      takeUntil(this.cancelRequests$),
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        const url = `${this.apiUrl}/daily-leads/${id}`;

        return this.http.get<DailyLeadShowResponse>(url, httpOptions).pipe(
          takeUntil(this.cancelRequests$),
          catchError(error => {
            console.error('Error fetching lead details', error);
            return throwError(() => error);
          })
        );
      })
    );
  }

  generateLeads(count: number, icpId: number): Observable<GenerateLeadsResponse> {
    return this.authService.token().pipe(
      takeUntil(this.cancelRequests$),
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          })
        };

        const url = `${this.apiUrl}/daily-leads/generate`;

        return this.http.post<GenerateLeadsResponse>(url, { count, icp_id: icpId }, httpOptions).pipe(
          takeUntil(this.cancelRequests$),
          catchError(error => {
            console.error('Error generating daily leads', error);
            return throwError(() => error);
          })
        );
      })
    );
  }

  getDailyLeadRuns(params?: {
    filter?: { icp_id?: number; status?: string; run_type?: string; run_date?: string };
    sort?: string;
    per_page?: number;
    page?: number;
  }): Observable<DailyLeadRunsResponse> {
    return this.authService.token().pipe(
      takeUntil(this.cancelRequests$),
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          }),
          params: new HttpParams()
        };

        if (params?.filter?.icp_id) {
          httpOptions.params = httpOptions.params.set('filter[icp_id]', params.filter.icp_id.toString());
        }
        if (params?.filter?.status) {
          httpOptions.params = httpOptions.params.set('filter[status]', params.filter.status);
        }
        if (params?.filter?.run_type) {
          httpOptions.params = httpOptions.params.set('filter[run_type]', params.filter.run_type);
        }
        if (params?.filter?.run_date) {
          httpOptions.params = httpOptions.params.set('filter[run_date]', params.filter.run_date);
        }
        if (params?.sort) {
          httpOptions.params = httpOptions.params.set('sort', params.sort);
        }
        if (params?.per_page) {
          httpOptions.params = httpOptions.params.set('per_page', params.per_page.toString());
        }
        if (params?.page) {
          httpOptions.params = httpOptions.params.set('page', params.page.toString());
        }

        const url = `${this.apiUrl}/daily-lead-runs`;

        return this.http.get<DailyLeadRunsResponse>(url, httpOptions).pipe(
          takeUntil(this.cancelRequests$),
          catchError(error => {
            console.error('Error fetching daily lead runs', error);
            return throwError(() => error);
          })
        );
      })
    );
  }

  getIcpAdjustmentNotification(): Observable<IcpAdjustmentNotification | null> {
    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }),
          params: new HttpParams().set('filter[type]', 'icp_adjustment_needed')
        };

        const url = `${this.apiUrl}/daily-leads/notifications`;

        return this.http.get<{ data: IcpAdjustmentNotification[] }>(url, httpOptions).pipe(
          map(response => response.data?.length ? response.data[0] : null),
          catchError(() => of(null))
        );
      }),
      catchError(() => of(null))
    );
  }

  getNotifications(params?: {
    page?: number;
    per_page?: number;
  }): Observable<DailyLeadNotificationsResponse> {
    return this.authService.token().pipe(
      takeUntil(this.cancelRequests$),
      switchMap(token => {
        this.token = token;

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          }),
          params: new HttpParams()
        };

        if (params?.per_page) {
          httpOptions.params = httpOptions.params.set('per_page', params.per_page.toString());
        }
        if (params?.page) {
          httpOptions.params = httpOptions.params.set('page', params.page.toString());
        }

        const url = `${this.apiUrl}/daily-leads/notifications`;

        return this.http.get<DailyLeadNotificationsResponse>(url, httpOptions).pipe(
          takeUntil(this.cancelRequests$),
          catchError(error => {
            console.error('Error fetching notifications', error);
            return throwError(() => error);
          })
        );
      })
    );
  }

  markAllNotificationsRead(): Observable<any> {
    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          })
        };

        const url = `${this.apiUrl}/daily-leads/notifications/read-all`;

        return this.http.post(url, {}, httpOptions).pipe(
          catchError(error => {
            console.error('Error marking notifications as read', error);
            return throwError(() => error);
          })
        );
      })
    );
  }

  cancelRequests(): void {
    this.cancelRequests$.next();
  }
}
